// ordinal.pipe.ts
import { Pipe, PipeTransform, inject, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

type GenderType = 'masculine' | 'feminine';

@Pipe({
  name: 'ordinal',
  pure: false
})
export class OrdinalPipe implements PipeTransform, OnDestroy {
  private translateService = inject(TranslateService);
  private currentLang = this.translateService.currentLang;
  private destroy$ = new Subject<void>();

  constructor() {
    this.translateService.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        this.currentLang = event.lang;
      });
  }

  transform(value: number, gender: GenderType = 'masculine'): string {
    if (isNaN(value)) {
      return '';
    }

    if (this.currentLang === 'ro') {
      return this.getRomanianOrdinal(value, gender);
    }

    return this.getEnglishOrdinal(value);
  }

  private getEnglishOrdinal(value: number): string {
    const remainder = value % 100;

    if (remainder >= 11 && remainder <= 13) {
      return value + 'th';
    }

    switch (value % 10) {
      case 1:
        return value + 'st';
      case 2:
        return value + 'nd';
      case 3:
        return value + 'rd';
      default:
        return value + 'th';
    }
  }

  private getRomanianOrdinal(value: number, gender: GenderType): string {
    // Special case for 1
    if (value === 1) {
      return gender === 'masculine' ? 'primul' : 'prima';
    }

    const prefix = gender === 'masculine' ? 'al ' : 'a ';
    const suffix = this.getRomanianSuffix(gender);
    const connector = this.getRomanianConnector(value, gender);

    return `${prefix}${value}${connector}${suffix}`;
  }

  private getRomanianSuffix(gender: GenderType): string {
    return gender === 'masculine' ? 'lea' : 'a';
  }

  private getRomanianConnector(value: number, gender: GenderType): string {
    if (value % 10 === 1 && value !== 1) {
      return gender === 'masculine' ? '-ulea' : '-a';
    }
    return '-';
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
